import { invalidateNotificationsQueryCache } from "@circle-react-shared/Notifications/useHeaderNotificationsApi";
import { useQueryClient } from "react-query";

import { t } from "@/i18n-js/instance";
import { useCanMessageMember } from "@/react/components/Profile/useCanMessageMember";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";
import { useSendMessageToUser } from "@/react/components/shared/MembersList/hooks/useSendMessageToUser";
import { useAcceptConnection } from "@/react/hooks/connections/useAcceptConnection";
import type { ProfileResponse } from "@/react/hooks/profile/useProfileInfo";

import {
  invalidateAllMemberQueries,
  optimisticallyRemoveConnectionRequest,
} from "./util";

interface Initiator {
  first_name: string;
  id: number;
  public_uid: string;
}

export const useAcceptConnectionForUser = ({
  initiator,
  onSuccess,
}: {
  initiator: Initiator;
  onSuccess?: () => void;
}) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const { shouldShowMessageOption } = useCanMessageMember();
  const shouldShowMessageCTA = shouldShowMessageOption(
    initiator as ProfileResponse,
  );

  const { handleMessageMember, isLoading: isMessaging } =
    useSendMessageToUser();

  const { mutate: acceptConnection, isPending: isAccepting } =
    useAcceptConnection({
      onMutate: (connectionId: string) => {
        optimisticallyRemoveConnectionRequest(queryClient, connectionId);
        toast.success(
          <div className="flex items-center gap-2">
            <span>
              {t("profile_drawer.connect_banner.messages.connection_accepted", {
                firstName: initiator.first_name,
              })}
            </span>
            {shouldShowMessageCTA && (
              <button
                type="button"
                className="hover:text-light underline transition-colors"
                onClick={async () => {
                  await handleMessageMember(initiator.id.toString());
                }}
              >
                {t("message")}
              </button>
            )}
          </div>,
          {
            duration: "long",
          },
        );
        onSuccess?.();
      },
      onSuccess: () => {
        void invalidateAllMemberQueries(queryClient, initiator.public_uid);
        invalidateNotificationsQueryCache({ queryClient });
      },
      onError: () => {
        toast.error(
          t("profile_drawer.connect_banner.messages.connection_accept_error"),
        );
        void invalidateAllMemberQueries(queryClient, initiator.public_uid);
        invalidateNotificationsQueryCache({ queryClient });
      },
    });

  return {
    acceptConnectionForUser: acceptConnection,
    isAccepting: isAccepting || isMessaging,
  };
};
